import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { transferApi, siteApi, safetyKitApi, stationeryApi, itApi } from '../../api';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useAuth } from '../../context/AuthContext';

// itemType is a form-only concept (not sent to the backend) that picks which
// of kitId/itemId/itItemId gets set on submit — mirrors the xor the backend
// enforces on Transfer itself.
const emptyCrossGroupForm = { itemType: 'material', fromSiteId: '', toSiteId: '', itemId: '', quantity: '', remarks: '' };

export default function TransferList() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const { user, hasPermission, isSiteRestricted } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState('');

  const canCrossGroup = hasPermission('cross_group_transfer');
  // IT transfers are isolated behind it_view same as the rest of the IT
  // section — a user without it_view never sees the IT option in the type
  // picker, and the backend independently hides itItemId-set rows from list().
  const canViewIT = hasPermission('it_view');
  const [sites, setSites] = useState([]);
  const [kits, setKits] = useState([]);
  const [stationeryItems, setStationeryItems] = useState([]);
  const [itItems, setItItems] = useState([]);
  const [crossGroupForm, setCrossGroupForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null); // transfer id currently mid-action, to disable its buttons and prevent double-clicks
  const [rejectConfirmId, setRejectConfirmId] = useState(null);

  const load = async () => {
    try {
      const params = tab === 'all' ? {} : { status: tab };
      const res = await transferApi.list(params);
      setTransfers(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]);

  useEffect(() => {
    if (!canCrossGroup) return;
    // All sites, deliberately unfiltered by group — this dialog is the one
    // place a user can move an item between two different site groups.
    const requests = [siteApi.list(), safetyKitApi.list(), stationeryApi.listItems({ isActive: true })];
    if (canViewIT) requests.push(itApi.listItems({ isActive: true }));
    Promise.all(requests)
      .then(([s, k, si, ii]) => {
        setSites(s.data.data || []);
        setKits(k.data.data || []);
        setStationeryItems(si.data.data || []);
        if (ii) setItItems(ii.data.data || []);
      })
      .catch(() => {});
  }, [canCrossGroup, canViewIT]);

  // The item list the form's "Item" dropdown draws from, keyed by the
  // selected itemType — mirrors the backend's kitId/itemId/itItemId xor.
  const itemsForType = (type) => (type === 'material' ? kits : type === 'stationery' ? stationeryItems : itItems);
  const idFieldForType = (type) => (type === 'material' ? 'kitId' : type === 'stationery' ? 'itemId' : 'itItemId');

  const handleCrossGroupSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await transferApi.create({
        fromSiteId: parseInt(crossGroupForm.fromSiteId),
        toSiteId:   parseInt(crossGroupForm.toSiteId),
        [idFieldForType(crossGroupForm.itemType)]: parseInt(crossGroupForm.itemId),
        quantity:   parseInt(crossGroupForm.quantity),
        remarks:    crossGroupForm.remarks || null,
      });
      setCrossGroupForm(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  // Shared handler for Approve/Reject/Send/Receive — guards against a stuck
  // or double-fired click (no feedback, or two concurrent requests for the
  // same transfer) the same way handleCrossGroupSubmit already does.
  const handleAction = async (id, apiCall) => {
    setBusyId(id);
    setError('');
    try {
      await apiCall();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed — the transfer may have already changed state. Refresh and try again.');
    } finally {
      setBusyId(null);
    }
  };

  // Send/Receive are site-scoped, not permission-scoped — only the storekeeper
  // of the relevant site (or super_admin) may act, mirroring the backend check.
  const canSend     = (t) => !isSiteRestricted || user?.siteId === t.fromSiteId;
  const canReceive  = (t) => !isSiteRestricted || user?.siteId === t.toSiteId;
  // Approval is permission-scoped (transfer_creation_approval), independent
  // of transfer_request — not site-scoped the way Send/Receive are.
  const canApprove  = hasPermission('transfer_creation_approval');

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        title="Transfers"
        subtitle="Inter-site transfers — Materials, Stationery, and IT"
        action={canCrossGroup && (
          <button className="btn-primary" onClick={() => setCrossGroupForm(emptyCrossGroupForm)}>
            Cross Group Transfer
          </button>
        )}
      />
      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      <div className="flex gap-2 mb-6">
        {['all', 'awaiting_approval', 'pending', 'sent', 'completed'].map((s) => (
          <button key={s} className={tab === s ? 'btn-primary' : 'btn-secondary'} onClick={() => { setTab(s); setLoading(true); }}>
            {s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      <div className="table-container">
        <table>
          <thead><tr><th>From</th><th>To</th><th>Item</th><th>Qty</th><th>Status</th><th>Requested By</th><th>Actions</th></tr></thead>
          <tbody>
            {transfers.map((t) => (
              <tr key={t.id} className="cursor-pointer" onClick={() => navigate(`/transfers/${t.id}`)}>
                <td className="font-medium">{t.fromSite?.name}</td>
                <td className="font-medium">{t.toSite?.name}</td>
                <td>{t.kit?.name || t.item?.name || t.itItem?.name}</td>
                <td className="font-semibold">{t.quantity}</td>
                <td><StatusBadge status={t.status} /></td>
                <td className="text-gray-500 text-sm">{t.requestedByUsername || '—'}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-2">
                    {t.status === 'awaiting_approval' && canApprove && (
                      <>
                        <button disabled={busyId === t.id} className="text-green-600 hover:underline text-sm disabled:opacity-50 disabled:no-underline" onClick={() => handleAction(t.id, () => transferApi.approve(t.id))}>
                          {busyId === t.id ? 'Approving…' : 'Approve'}
                        </button>
                        <button disabled={busyId === t.id} className="text-red-600 hover:underline text-sm disabled:opacity-50 disabled:no-underline" onClick={() => setRejectConfirmId(t.id)}>
                          {busyId === t.id ? 'Rejecting…' : 'Reject'}
                        </button>
                      </>
                    )}
                    {t.status === 'pending' && canSend(t) && (
                      <button disabled={busyId === t.id} className="text-blue-600 hover:underline text-sm disabled:opacity-50 disabled:no-underline" onClick={() => handleAction(t.id, () => transferApi.send(t.id))}>
                        {busyId === t.id ? 'Sending…' : 'Mark as Sent'}
                      </button>
                    )}
                    {t.status === 'sent' && canReceive(t) && (
                      <button disabled={busyId === t.id} className="text-green-600 hover:underline text-sm disabled:opacity-50 disabled:no-underline" onClick={() => handleAction(t.id, () => transferApi.receive(t.id))}>
                        {busyId === t.id ? 'Receiving…' : 'Mark as Received'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Cross-Group Transfer Modal ───────────────────────────────────────── */}
      {crossGroupForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-1">Cross Group Transfer</h3>
            <p className="text-xs text-gray-500 mb-4">Move an item between any two sites, regardless of group.</p>
            <form onSubmit={handleCrossGroupSubmit} className="space-y-4">
              <div>
                <label className="label">Item Type</label>
                <div className="flex gap-1">
                  {[['material', 'Material'], ['stationery', 'Stationery'], ...(canViewIT ? [['it', 'IT']] : [])].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      className={crossGroupForm.itemType === v ? 'btn-primary text-sm' : 'btn-secondary text-sm'}
                      onClick={() => setCrossGroupForm({ ...crossGroupForm, itemType: v, itemId: '' })}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">From Site</label>
                <SearchableSelect
                  options={sites.filter((s) => String(s.id) !== String(crossGroupForm.toSiteId)).map((s) => ({ id: s.id, label: `${s.name} (${s.group?.name || 'no group'})` }))}
                  value={crossGroupForm.fromSiteId}
                  onChange={(id) => setCrossGroupForm({ ...crossGroupForm, fromSiteId: id })}
                  placeholder="Select site…"
                  required
                />
              </div>
              <div>
                <label className="label">To Site</label>
                <SearchableSelect
                  options={sites.filter((s) => String(s.id) !== String(crossGroupForm.fromSiteId)).map((s) => ({ id: s.id, label: `${s.name} (${s.group?.name || 'no group'})` }))}
                  value={crossGroupForm.toSiteId}
                  onChange={(id) => setCrossGroupForm({ ...crossGroupForm, toSiteId: id })}
                  placeholder="Select site…"
                  required
                />
              </div>
              <div>
                <label className="label">Item</label>
                <select className="input" value={crossGroupForm.itemId} onChange={(e) => setCrossGroupForm({ ...crossGroupForm, itemId: e.target.value })} required>
                  <option value="">Select item…</option>
                  {itemsForType(crossGroupForm.itemType).map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Quantity</label>
                <input className="input" type="number" min="1" value={crossGroupForm.quantity} onChange={(e) => setCrossGroupForm({ ...crossGroupForm, quantity: e.target.value })} required />
              </div>
              <div>
                <label className="label">Remarks</label>
                <textarea className="input" value={crossGroupForm.remarks} onChange={(e) => setCrossGroupForm({ ...crossGroupForm, remarks: e.target.value })} rows={2} />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => { setCrossGroupForm(null); setError(''); }}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Submitting…' : 'Create Transfer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={rejectConfirmId != null}
        title="Reject Transfer"
        message="Reject this transfer request?"
        confirmLabel="Reject"
        onConfirm={() => { const id = rejectConfirmId; setRejectConfirmId(null); handleAction(id, () => transferApi.cancel(id)); }}
        onCancel={() => setRejectConfirmId(null)}
      />
    </div>
  );
}
